import styled, { keyframes } from "styled-components";
import { duration, easing } from "@magicbi/ui-library";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  borderWidth,
  borderStyle,
  borderRadius,
} from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import type { SidebarProfileChipPlacement } from "./SidebarProfileChip.types";
import { shadows } from "../../../design-system/tokens/shadows";

const MENU_WIDTH = "220px";

/* GAP: design 11px initials in the stacked avatar; no 11px type token. */
const STACKED_INITIALS_SIZE = "0.6875rem";

export const Wrapper = styled.div<{ $compact?: boolean }>`
  position: relative;
  width: ${({ $compact }) => ($compact ? "auto" : "100%")};
`;

export const Trigger = styled.button<{ $compact?: boolean; $stacked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $compact }) => ($compact ? "center" : "flex-start")};
  gap: ${({ $stacked }) => ($stacked ? spacing[2.5] : spacing[2])};
  width: ${({ $compact }) => ($compact ? "40px" : "100%")};
  height: ${({ $compact }) => ($compact ? "40px" : "auto")};
  padding: ${({ $compact, $stacked }) => ($compact ? "0" : $stacked ? "0" : `${spacing[2]} ${spacing[3]}`)};
  background: transparent;
  border: 0;
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover,
  &:focus-visible {
    background: ${rawColors.structural[100]};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[500]};
    outline-offset: -2px;
  }
`;

/** Photo + initials use the same 28×28 round container (Figma profile chip size). */
export const AvatarShell = styled.span<{ $stacked?: boolean }>`
  width: ${({ $stacked }) => ($stacked ? spacing[8] : "28px")};
  height: ${({ $stacked }) => ($stacked ? spacing[8] : "28px")};
  flex-shrink: 0;
  border-radius: ${borderRadius.full};
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const PhotoAvatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const InitialsAvatar = styled.span<{ $stacked?: boolean }>`
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $stacked }) => ($stacked ? rawColors.brand.primaryDarker : rawColors.primary[100])};
  color: ${({ $stacked }) => ($stacked ? rawColors.structural.white : rawColors.primary[500])};
  ${typographyMixin("caption", 3)}
  ${({ $stacked }) => ($stacked ? `font-size: ${STACKED_INITIALS_SIZE}; font-weight: 700;` : "")}
  text-transform: uppercase;
  user-select: none;
`;

/** Single-line "My profile" label; ellipsis if a longer label is passed. */
export const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The two-line text of the stacked chip: a bold label over a quiet line. */
export const Texts = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const StackedLabel = styled.span`
  ${typographyMixin("caption", 1)}
  color: ${rawColors.text.highEmphasis};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SubLabel = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Chevron rotates 180° when the menu is open. */
export const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${rawColors.text.lowEmphasis};
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});

  & > svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const menuIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: none; }
`;

/**
 * Menu opens UPWARD by default; fixed placements get top/left inline from the chip state — chip sits at the bottom of the sidebar. Compact
 * (icon-only) trigger anchors the menu to its right edge instead of
 * stretching to the trigger's 40px width.
 */
export const Menu = styled.div<{ $compact?: boolean; $placement?: SidebarProfileChipPlacement }>`
  position: ${({ $placement }) => ($placement && $placement !== "top-start" ? "fixed" : "absolute")};
  ${({ $placement, $compact }) =>
    $placement && $placement !== "top-start"
      ? `width: ${MENU_WIDTH};`
      : `bottom: calc(100% + ${spacing[1]});
  left: ${$compact ? "auto" : "0"};
  right: 0;
  width: ${$compact ? MENU_WIDTH : "auto"};`}
  background: ${rawColors.structural.white};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.component.card};
  padding: ${spacing[1]} 0;
  z-index: 1000;
  transform-origin: bottom left;
  animation: ${menuIn} ${duration.fast} ${easing.slideIn} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Non-interactive header row inside the menu showing the real user identity. */
export const IdentityHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: ${spacing[2]} ${spacing[3]};
  border-bottom: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
`;

export const IdentityName = styled.span`
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const IdentityEmail = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SegmentedRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding: ${spacing[2]} ${spacing[3]};
`;

export const SegmentedLabel = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
`;

export const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  width: 100%;
  padding: ${spacing[2]} ${spacing[3]};
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};

  & svg {
    color: ${rawColors.text.mediumEmphasis};
  }

  &:hover,
  &:focus-visible {
    background: ${rawColors.structural[100]};
  }

  &:focus-visible {
    outline: none;
  }
`;

export const SectionLabel = styled.div`
  padding: ${spacing[2]} ${spacing[3]} ${spacing[1]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-top: 1px solid ${rawColors.structural[100]};
`;

export const SectionItem = styled(MenuItem)`
  transition: background ${duration.fast} ${easing.slideIn};

  & > svg {
    flex-shrink: 0;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background: transparent;
  }
`;

export const ItemText = styled.span`
  flex: 1 0 auto;
  white-space: nowrap;
`;

export const ItemHint = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
  min-width: 0;
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StatusDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${rawColors.accent.green500};
  }
`;
